import { czyWerdykt } from "@shared/recenzjeTypy.js";
import type { KorektaSzkic } from "./recenzja.js";
import { odczytajSzkicUwagi, type SzkicUwagi } from "./uwagiWypowiedzi.js";

/**
 * Szkice recenzenta, ktorych serwer jeszcze nie przyjal, w sessionStorage. Po co: zapis moze sie nie udac (siec, 5xx,
 * przeliczana ocena), a recenzent w tym czasie przechodzi do innej rozmowy albo przeladowuje strone. Bez kopii tekst
 * (komentarz, wlasna wersja, notatka know-how do 20 tys. znakow) znikalby bez ostrzezenia.
 *
 * ZASADY:
 * - Klucz wpisu: token + id rozmowy + slot kolejki zapisu. Dzieki temu szkice dwoch rozmow i dwoch linkow sie nie mieszaja.
 * - Wpis powstaje przy kazdej zmianie pola i znika dopiero po UDANYM zapisie tej samej wartosci. Nowszy szkic, ktory
 *   przyszedl w miedzyczasie, zostaje.
 * - sessionStorage bywa zablokowane (tryb prywatny, wylaczone dane strony), pelne albo nie istnieje: kazde wywolanie jest
 *   w try/catch, a brak magazynu to po prostu brak kopii. Zapis na serwer nie zalezy od magazynu.
 * - Z magazynu czytamy niezaufane dane (ktos mogl je zmienic w DevTools): przed uzyciem kazdy wpis przechodzi walidacje
 *   ksztaltu, a niepoprawny jest ignorowany.
 */

const PREFIKS = "convolens:szkic:";

/** Slot kolejki zapisu dla korekty wskazania o danym kluczu. */
export const kluczSlotuKorekty = (klucz: string): string => `w:${klucz}`;
/** Slot kolejki zapisu dla notatki know-how do rozmowy. */
export const SLOT_NOTATKI = "notatka";
/** Slot kolejki zapisu dla uwagi recenzenta przy wypowiedzi (klucz uwagi: UwagaRecenzji.klucz). */
export const kluczSlotuUwagi = (klucz: string): string => `u:${klucz}`;

/** Co kolejka wie o trwalosci: zapis przy zmianie i usuniecie po udanym zapisie tej samej wartosci. */
export interface MagazynSlotow {
  zapisz: (slot: string, wartosc: unknown) => void;
  usunJesliTa: (slot: string, wartosc: unknown) => void;
}

/** sessionStorage albo null, gdy go nie ma lub dostep rzuca (np. SecurityError). */
function domyslnyMagazyn(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export class MagazynSzkicow implements MagazynSlotow {
  private readonly prefiks: string;

  constructor(
    token: string,
    sesjaId: string,
    private readonly magazyn: () => Storage | null = domyslnyMagazyn,
  ) {
    this.prefiks = `${PREFIKS}${encodeURIComponent(token)}:${encodeURIComponent(sesjaId)}:`;
  }

  zapisz = (slot: string, wartosc: unknown): void => {
    try {
      this.magazyn()?.setItem(this.prefiks + slot, JSON.stringify(wartosc));
    } catch {
      // Magazyn zablokowany albo pelny: zapis na serwer dziala dalej, brakuje tylko kopii.
    }
  };

  usunJesliTa = (slot: string, wartosc: unknown): void => {
    try {
      const magazyn = this.magazyn();
      if (!magazyn) return;
      const klucz = this.prefiks + slot;
      // Wpis z innej, nowszej wartosci (np. szkic zapisany przez swiezo zamontowany widok) nie moze zniknac razem z tym,
      // co wlasnie przyjal serwer.
      if (magazyn.getItem(klucz) === JSON.stringify(wartosc)) magazyn.removeItem(klucz);
    } catch {
      // Jak wyzej.
    }
  };

  /** Kasuje wpis bez warunku (kopia, ktora nie ma juz sensu: pusta tresc, uwaga, ktorej nie ma na serwerze). */
  usun = (slot: string): void => {
    try {
      this.magazyn()?.removeItem(this.prefiks + slot);
    } catch {
      // Jak wyzej.
    }
  };

  /** Wszystkie wpisy tej rozmowy: slot -> wartosc po JSON.parse (nieczytelne wpisy pomijamy). */
  wczytaj = (): Map<string, unknown> => {
    const wynik = new Map<string, unknown>();
    try {
      const magazyn = this.magazyn();
      if (!magazyn) return wynik;
      // Najpierw zbieramy klucze: indeksy przesuwaja sie, gdy cos usuniemy w trakcie petli.
      const klucze: string[] = [];
      for (let i = 0; i < magazyn.length; i++) {
        const klucz = magazyn.key(i);
        if (klucz !== null && klucz.startsWith(this.prefiks)) klucze.push(klucz);
      }
      for (const klucz of klucze) {
        try {
          const surowy = magazyn.getItem(klucz);
          if (surowy !== null) wynik.set(klucz.slice(this.prefiks.length), JSON.parse(surowy));
        } catch {
          // Uszkodzony JSON: wpis jest bezuzyteczny.
        }
      }
    } catch {
      // Magazyn niedostepny: brak szkicow do przywrocenia.
    }
    return wynik;
  };
}

/** Szkic korekty z magazynu albo null, gdy ksztalt sie nie zgadza (zle pole, zly typ werdyktu). */
export function odczytajSzkicKorekty(surowy: unknown): KorektaSzkic | null {
  if (typeof surowy !== "object" || surowy === null) return null;
  const { werdykt, komentarz, wariant } = surowy as Record<string, unknown>;
  if (werdykt !== null && !czyWerdykt(werdykt)) return null;
  if (typeof komentarz !== "string" || typeof wariant !== "string") return null;
  return { werdykt: werdykt ?? null, komentarz, wariant };
}

export interface SzkiceRozmowy {
  /** Klucz wskazania (bez prefiksu slotu) -> szkic. */
  korekty: ReadonlyMap<string, KorektaSzkic>;
  /** Szkic notatki albo null, gdy go nie ma. Pusty napis jest wartoscia (czyszczenie notatki), wiec null to brak. */
  notatka: string | null;
  /** Klucz uwagi (bez prefiksu slotu) -> szkic uwagi przy wypowiedzi. */
  uwagi: ReadonlyMap<string, SzkicUwagi>;
}

/** Rozklada wpisy magazynu na szkice korekt, uwag i notatke. Nieznane sloty i niepoprawne ksztalty ida do kosza. */
export function rozlozSzkice(wpisy: ReadonlyMap<string, unknown>): SzkiceRozmowy {
  const korekty = new Map<string, KorektaSzkic>();
  const uwagi = new Map<string, SzkicUwagi>();
  let notatka: string | null = null;
  for (const [slot, surowy] of wpisy) {
    if (slot === SLOT_NOTATKI) {
      if (typeof surowy === "string") notatka = surowy;
    } else if (slot.startsWith("w:")) {
      const szkic = odczytajSzkicKorekty(surowy);
      if (szkic) korekty.set(slot.slice(2), szkic);
    } else if (slot.startsWith("u:")) {
      const szkic = odczytajSzkicUwagi(surowy);
      if (szkic) uwagi.set(slot.slice(2), szkic);
    }
  }
  return { korekty, notatka, uwagi };
}

export const BRAK_SZKICOW: SzkiceRozmowy = { korekty: new Map(), notatka: null, uwagi: new Map() };
